import { css } from '@emotion/core';
import {
    Avatar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tab,
    Tabs,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { BugReport, CheckCircleOutline } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import ReactJsonView from '@microlink/react-json-view';
import React from 'react';

import { downloadFile } from '../../api';
import { notificationTime } from '../../common/timestamp';
import { useSlides } from '../../hooks/use-candidate-slides';
import { useModal } from '../../hooks/use-modal';
import { usePromptInfo } from '../../hooks/use-prompt-info';
import { useSession } from '../../hooks/use-session';
import { notificationStyles } from './notification-styles';

const styles = (theme: Theme) => css`
    ${notificationStyles(theme)}

    .item-actions {
        display: flex;
        position: absolute;
        bottom: 10px;
        right: 12px;
        opacity: 0;

        .MuiIconButton-root {
            margin-left: 5px;
        }
    }

    &:hover {
        background: rgba(0, 0, 0, 0.025);
        .item-actions {
            opacity: 1;
        }
    }
`;

const dialogStyles = css`
    .MuiDialogContent-root {
        padding-top: 0;

        .MuiList-root {
            margin-bottom: 12px;
        }
    }

    .MuiPaper-root {
        max-height: calc(100vh - 96px);
        height: calc(100vh - 96px);
    }

    .dialog-title {
        display: flex;
        justify-content: space-between;
    }

    .json-view-container {
        overflow: auto;
        max-height: calc(100vh - 400px);
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 8px;
    }
`;

export const ProfileMergeValidationCheckNotification: React.FC<{
    data: {
        issues: string[];
        newProfileDataKey: string;
        existingDataKey: string;
        mergedDataKey: string;
        url: string;
        appVersion: string;
        personId?: string;
        inferenceId?: string;
        newSource?: string;
    };
    userId: string;
    createdAt: number;
    onComplete: () => void;
}> = ({ data, userId, createdAt, onComplete }) => {
    const theme = useTheme();
    const { user } = useSession();
    const { setList } = useSlides();
    const { showPromptInfo } = usePromptInfo();
    const { setAlert } = useModal();
    const [detailsOpen, setDetailsOpen] = React.useState(false);
    const [selectedTab, setSelectedTab] = React.useState(0);
    const [newProfile, setNewProfile] = React.useState<any | null>(null);
    const [existingProfile, setExistingProfile] = React.useState<any | null>(null);
    const [mergedProfile, setMergedProfile] = React.useState<any | null>(null);
    const [loading, setLoading] = React.useState(false);

    const handleOpenDetails = async () => {
        setDetailsOpen(true);
        setLoading(true);
        try {
            const [newResponse, existingResponse, mergedResponse] = await Promise.all([
                downloadFile(data.newProfileDataKey),
                downloadFile(data.existingDataKey),
                downloadFile(data.mergedDataKey)
            ]);
            const [newJson, existingJson, mergedJson] = await Promise.all([
                newResponse.json(),
                existingResponse.json(),
                mergedResponse.json()
            ]);
            setNewProfile(newJson);
            setExistingProfile(existingJson);
            setMergedProfile(mergedJson);
        } catch (error) {
            setAlert('Error', 'Failed to load profile data: ' + error?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedTab(0);
        setNewProfile(null);
        setExistingProfile(null);
        setMergedProfile(null);
    };

    const handleShowPersonCard = () => {
        if (data.personId) {
            setList([{ personId: data.personId }], { personId: data.personId });
        }
    };

    const handleShowInference = () => {
        if (data.inferenceId) {
            showPromptInfo({ inferenceId: data.inferenceId });
        }
    };

    const handleMarkComplete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onComplete();
    };

    const handleTabChange = (_1: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
    };

    const actions =
        user.id === userId ? (
            <div className="item-actions">
                <Tooltip title="OK">
                    <IconButton size="small" onClick={handleMarkComplete}>
                        <CheckCircleOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
        ) : null;

    const issuesList = data.issues.map((issue, i) => (
        <ListItem disableGutters={true} key={i}>
            <ListItemText primary={issue} />
        </ListItem>
    ));

    const showPersonCardButton = data.personId ? (
        <Button onClick={handleShowPersonCard}>View Person Card</Button>
    ) : null;

    const showInferenceButton = data.inferenceId ? <Button onClick={handleShowInference}>View Prompts</Button> : null;

    let jsonViewer;
    if (loading) {
        jsonViewer = <Skeleton variant="rect" height="300px" />;
    } else {
        let content;
        switch (selectedTab) {
            case 0:
                content = newProfile;
                break;
            case 1:
                content = existingProfile;
                break;
            case 2:
                content = mergedProfile;
                break;
            default:
                content = null;
        }

        if (content) {
            jsonViewer = (
                <div className="json-view-container">
                    <ReactJsonView
                        src={content}
                        theme="rjv-default"
                        collapsed={2}
                        displayDataTypes={false}
                        enableClipboard={true}
                        displayObjectSize={true}
                        name={false}
                        sortKeys={true}
                    />
                </div>
            );
        }
    }

    const detailsDialog = (
        <Dialog open={detailsOpen} maxWidth="xl" fullWidth={true} css={dialogStyles}>
            <DialogTitle>
                <Typography variant="h4" component="div" className="dialog-title">
                    <span>Profile Merge Validation Issues</span>
                    <span>
                        {data.newSource} ({data.appVersion})
                    </span>
                </Typography>
            </DialogTitle>
            <DialogContent>
                <List disablePadding={true} dense={true}>
                    {issuesList}
                </List>
                <Tabs value={selectedTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                    <Tab label="New Profile" />
                    <Tab label="Existing Cached" />
                    <Tab label="Merged Result" />
                </Tabs>
                {jsonViewer}
            </DialogContent>
            <DialogActions>
                {showInferenceButton}
                <Button
                    href={`https://${data.url?.replace(/^https?:\/\//, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Visit LinkedIn Page
                </Button>
                {showPersonCardButton}
                <Button onClick={onComplete}>Clear</Button>
                <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    const primary = <Typography variant="h6">Profile Merge Validation Issues</Typography>;

    return (
        <>
            <ListItem css={styles(theme)} onClick={handleOpenDetails} button={true}>
                <ListItemAvatar>
                    <Avatar>
                        <BugReport />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText disableTypography={true} primary={primary} />
                <div>
                    <Typography
                        color="textSecondary"
                        noWrap={true}
                        variant="caption"
                        className="caption"
                        component="div"
                    >
                        <span className="left-pad">{notificationTime(createdAt)}</span>
                    </Typography>
                </div>
                {actions}
            </ListItem>
            {detailsDialog}
        </>
    );
};
